import React from 'react';
import { Button, Divider, Typography, styled } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';

const Card = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  padding: theme.spacing(3),
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderTop: `4px solid ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
}));

const Row = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
});

const Total = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  '& .label': {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
}));

const Notice = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5),
  background: '#fffbea',
  border: '1px solid #f6e9b2',
  borderRadius: theme.shape.borderRadius,
  '& svg': {
    fontSize: 16,
    marginTop: 1,
    color: theme.palette.text.secondary,
  },
}));

export interface FormSummaryItem {
  label: string;
  value: string;
}

interface FormSummaryProps {
  title: string;
  items: FormSummaryItem[];
  totalLabel: string;
  total: string;
  submitLabel: string;
  submitting?: boolean;
  notice?: string;
}

const FormSummary: React.FC<FormSummaryProps> = ({
  title,
  items,
  totalLabel,
  total,
  submitLabel,
  submitting = false,
  notice,
}) => {
  return (
    <Card>
      <Typography variant="caption" fontWeight={700} color="textSecondary" sx={{ letterSpacing: 0.5 }}>
        {title.toUpperCase()}
      </Typography>

      {items.map(item => (
        <Row key={item.label}>
          <Typography variant="body2" color="textSecondary">
            {item.label}
          </Typography>
          <Typography variant="body2" color="textSecondary" fontWeight={600}>
            {item.value}
          </Typography>
        </Row>
      ))}

      <Divider />

      <Total>
        <span className="label">{totalLabel}</span>
        <Typography variant="h5" fontWeight={800} color="primary">
          {total}
        </Typography>
      </Total>

      <Button type="submit" variant="contained" color="primary" size="large" fullWidth disabled={submitting}>
        {submitting ? 'Salvando...' : submitLabel}
      </Button>

      {notice && (
        <Notice>
          <InfoOutlined />
          <Typography variant="caption" color="textSecondary">
            {notice}
          </Typography>
        </Notice>
      )}
    </Card>
  );
};

export default FormSummary;
